import { spinnerRing } from '../utils/bookingUtils';
import { POS_RESERVE_ENABLED_KEY } from '../constants/posConst';
import '../css/PosAdmin.css';

/**
 * The Feature Settings tab of the client form: switches an administrator
 * turns on or off for one client.
 *
 * HERE rather than on the club's own Product Settings screen, because these
 * change what the club gets rather than how it likes its screens laid out —
 * the club's store admin sees the result, not the switch.
 *
 * Controlled: the form owns the values and saves them with everything else,
 * so nothing is written until the administrator presses save.
 *
 * @param {object} props
 * @param {{reserveEnabled: boolean}} props.value - the switches as edited
 * @param {(next: {reserveEnabled: boolean}) => void} props.onChange
 * @param {boolean} [props.loading] - the stored values are still being read
 * @param {Error|null} [props.loadError] - the stored values could not be read;
 *   the tab then shows why and the form leaves them untouched on save
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
function PosClientFeatureSettingsTab({ value, onChange, loading = false, loadError = null, disabled = false }) {
  if (loading) {
    return <div className="pa-loading pa-loading-inline">{spinnerRing('Loading feature settings')}</div>;
  }

  if (loadError) {
    return (
      <div className="pa-form-grid">
        <div className="pa-field pa-field-wide">
          <span className="pa-field-label">Feature settings</span>
          <p className="pa-field-hint">
            This client&rsquo;s feature settings could not be read: {loadError.message}
          </p>
          <p className="pa-field-hint">
            They are being left exactly as they are — saving this form will not change them.
          </p>
        </div>
      </div>
    );
  }

  const reserveOn = value[POS_RESERVE_ENABLED_KEY];

  return (
    <div className="pa-form-grid">
      <div className="pa-field pa-field-wide">
        <span className="pa-field-label">Reserve (storeroom) stock</span>
        <label className={`pa-choice${reserveOn ? ' active' : ''}`}>
          <input
            type="checkbox"
            checked={reserveOn}
            onChange={(event) => onChange({ ...value, [POS_RESERVE_ENABLED_KEY]: event.target.checked })}
            disabled={disabled}
          />
          <span className="pa-choice-text">
            <span className="pa-choice-label">
              {reserveOn ? 'On — keep a reserve behind the shelf' : 'Off — one standalone stock count'}
            </span>
            <span className="pa-choice-desc">
              {reserveOn
                ? 'Products are registered into the reserve, and any stock added to the shelf (Active) is taken out of the reserve. Adding more than the reserve holds is refused.'
                : 'Inventory shows a single stock list. Products are registered and stocked straight into it. Existing reserve figures are kept, untouched, in case this is switched back on.'}
            </span>
          </span>
        </label>
        <span className="pa-field-hint">
          Applies to this client&rsquo;s Inventory and Products screens as soon as it is saved,
          including tills already open. The club cannot change this itself.
        </span>
      </div>
    </div>
  );
}

export default PosClientFeatureSettingsTab;
